import React from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import ThemeSwitcher from '../components/ThemeSwitcher';
import { useTheme } from '../contexts/ThemeContext';
import { defaultTheme, getTheme, ThemeName } from '../themes/themes';
import './ThemeSettingsPage.css';

const ThemeSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme, currentTheme, setTheme } = useTheme();
  const isDefaultTheme = currentTheme === defaultTheme;

  return (
    <div className="theme-settings-page">
      <CustomerHeader
        variant="sticky"
        showBackButton
        onBackClick={() => navigate('/app')}
        showProfileDropdown
      />
      
      <div className="theme-settings-container">
        <div className="theme-settings-header">
          <h1>Theme Settings</h1>
          <p>Customize the look and feel of the app</p>
        </div>

        <div className="theme-settings-content">
          <div className="theme-settings-section">
            <h2>Choose Your Theme</h2>
            <p className="section-description">
              Select a theme that matches your preference. Your choice will be saved automatically.
            </p>
            
            <div className="theme-switcher-container">
              <div className="current-theme-display">
                <label htmlFor="theme-switcher" className="theme-switcher-label">
                  Current Theme: <strong>{theme.displayName}</strong>
                  {isDefaultTheme && <span className="default-badge">(Default)</span>}
                </label>
                {!isDefaultTheme && (
                  <button
                    className="reset-default-btn"
                    onClick={() => setTheme(defaultTheme)}
                    title="Reset to default theme (Pure White - Original)"
                  >
                    ↺ Reset to Default
                  </button>
                )}
              </div>
              <div className="theme-switcher-wrapper">
                <ThemeSwitcher />
              </div>
              <p className="theme-switcher-help">
                Your current theme is <strong>{theme.displayName}</strong>. 
                {!isDefaultTheme && ' Click "Reset to Default" to return to Pure White theme (original).'}
              </p>
            </div>
            
            {/* All Theme Options Grid - Visual Selection */}
            <div className="theme-options-grid">
              {(['pure-white', 'sky-light', 'cloud-white', 'ocean-breeze', 'sunset-glow', 'arctic-fresh', 'neutral-elegance'] as ThemeName[]).map((themeName) => {
                const optionTheme = getTheme(themeName);
                const isActive = currentTheme === themeName;
                
                return (
                  <div
                    key={themeName}
                    className={`theme-option-card ${isActive ? 'active' : ''}`}
                    onClick={() => setTheme(themeName)}
                  >
                    <div 
                      className="theme-option-preview-large"
                      style={{ backgroundColor: optionTheme.colors.background }}
                    >
                      <div 
                        className="theme-option-preview-section"
                        style={{ backgroundColor: optionTheme.colors.sectionBackground }}
                      >
                        <div 
                          className="theme-option-preview-card"
                          style={{ backgroundColor: optionTheme.colors.card }}
                        ></div>
                      </div>
                    </div>
                    <h3 style={{ margin: '1rem 0 0.5rem 0', color: optionTheme.colors.text, fontSize: '2.5rem' }}>
                      {optionTheme.displayName}
                    </h3>
                    <p style={{ margin: 0, color: optionTheme.colors.textSecondary, fontSize: '1.75rem', minHeight: '5rem' }}>
                      {optionTheme.description}
                    </p>
                    {isActive && (
                      <div style={{ 
                        marginTop: '0.75rem', 
                        color: optionTheme.colors.primary, 
                        fontWeight: 600,
                        fontSize: '1.75rem'
                      }}>
                        ✓ Currently Active
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="theme-preview-section">
            <h2>Theme Preview</h2>
            <p className="section-description">
              See how your selected theme looks with sample content.
            </p>
            
            <div 
              className="theme-preview-box"
              style={{
                backgroundColor: theme.colors.background,
                borderColor: theme.colors.border,
              }}
            >
              <div 
                className="theme-preview-section-box"
                style={{ backgroundColor: theme.colors.sectionBackground }}
              >
                <div 
                  className="theme-preview-card-box"
                  style={{ backgroundColor: theme.colors.card }}
                >
                  <h3 style={{ color: theme.colors.text }}>Sample Card</h3>
                  <p style={{ color: theme.colors.textSecondary }}>
                    This is how content appears in the {theme.displayName} theme.
                  </p>
                  <button
                    className="theme-preview-button"
                    style={{
                      backgroundColor: theme.colors.primary,
                      color: 'white',
                    }}
                  >
                    Primary Button
                  </button>
                  <button
                    className="theme-preview-button secondary"
                    style={{
                      backgroundColor: theme.colors.secondary,
                      color: 'white',
                    }}
                  >
                    Secondary Button
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="theme-info-section">
            <h2>About Themes</h2>
            <p className="section-description">
              Themes change the background colors, section backgrounds, and borders throughout the app.
              Your brand colors (blue and orange) remain consistent across all themes.
            </p>
            <div className="theme-info-grid">
              <div className="info-item">
                <span className="info-icon">🎨</span>
                <div>
                  <strong>7 Theme Options</strong>
                  <p>Choose from Pure White (original), Sky Light, Cloud White, Ocean Breeze, Sunset Glow, Arctic Fresh, or Neutral Elegance</p>
                </div>
              </div>
              <div className="info-item">
                <span className="info-icon">💾</span>
                <div>
                  <strong>Auto-Save</strong>
                  <p>Your theme preference is automatically saved and will persist across sessions</p>
                </div>
              </div>
              <div className="info-item">
                <span className="info-icon">🔄</span>
                <div>
                  <strong>Instant Preview</strong>
                  <p>See changes immediately as you switch between themes</p>
                </div>
              </div>
            </div>
          </div>

          <div className="theme-settings-actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate('/theme-preview')}
            >
              View All Themes Side-by-Side →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeSettingsPage;

